import { useCallback } from "react";

export default function ProductEditorActions({
  parseMultilineUrls,
  createEmptyProductEditorDraft,
  toProductEditorDraft,
  adminFetch,
  apiBaseUrl,
  readApiJson,
  newProduct,
  productEditorOriginalUploadUrls,
  setAdminError,
  allowedTabs,
  setAdminTab,
  setEditingProductId,
  setProductEditorOriginalUploadUrls,
  setNewProduct
}) {
  const collectManagedUploadUrlsFromDraft = useCallback((draft) => {
    const values = [
      draft?.imageUrl,
      draft?.detailVideoUrl,
      ...parseMultilineUrls(draft?.mainImagesText),
      ...parseMultilineUrls(draft?.detailImagesText)
    ];

    return Array.from(new Set(values.filter((item) => String(item || "").includes("/uploads/"))));
  }, [parseMultilineUrls]);

  const collectManagedUploadUrlsFromProduct = useCallback((product) => {
    if (!product) {
      return [];
    }

    return Array.from(new Set([
      product.imageUrl,
      product.detailVideoUrl,
      ...(Array.isArray(product.mainImages) ? product.mainImages : []),
      ...(Array.isArray(product.detailImages) ? product.detailImages : [])
    ].filter((item) => String(item || "").includes("/uploads/"))));
  }, []);

  const resetProductEditorState = useCallback(() => {
    setEditingProductId(null);
    setProductEditorOriginalUploadUrls([]);
    setNewProduct(createEmptyProductEditorDraft());
  }, [createEmptyProductEditorDraft, setEditingProductId, setNewProduct, setProductEditorOriginalUploadUrls]);

  const openCreateProductEditor = useCallback(() => {
    resetProductEditorState();
    setAdminTab("productCreate");
  }, [resetProductEditorState, setAdminTab]);

  const startEditProduct = useCallback((product) => {
    setEditingProductId(product.id);
    setProductEditorOriginalUploadUrls(collectManagedUploadUrlsFromProduct(product));
    setNewProduct(toProductEditorDraft(product));
    setAdminTab("productCreate");
  }, [collectManagedUploadUrlsFromProduct, setAdminTab, setEditingProductId, setNewProduct, setProductEditorOriginalUploadUrls, toProductEditorDraft]);

  const closeProductEditor = useCallback(async () => {
    const deleteAdminProductAssets = async (urls) => {
      const uniqueUrls = Array.from(new Set((Array.isArray(urls) ? urls : []).filter(Boolean)));
      if (!uniqueUrls.length) {
        return;
      }

      const response = await adminFetch(`${apiBaseUrl}/admin/uploads/product-assets`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls: uniqueUrls })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Asset cleanup failed");
      }
    };

    const currentDraftUrls = collectManagedUploadUrlsFromDraft(newProduct);
    const urlsToDelete = currentDraftUrls.filter((item) => !productEditorOriginalUploadUrls.includes(item));
    try {
      await deleteAdminProductAssets(urlsToDelete);
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Asset cleanup failed");
      }
    }
    resetProductEditorState();
    if (allowedTabs.includes("products")) {
      setAdminTab("products");
    }
  }, [
    adminFetch,
    apiBaseUrl,
    allowedTabs,
    collectManagedUploadUrlsFromDraft,
    newProduct,
    productEditorOriginalUploadUrls,
    readApiJson,
    resetProductEditorState,
    setAdminError,
    setAdminTab
  ]);

  return {
    resetProductEditorState,
    openCreateProductEditor,
    startEditProduct,
    closeProductEditor,
    collectManagedUploadUrlsFromDraft,
    collectManagedUploadUrlsFromProduct
  };
}
